// ============================================================================
// EhrIntegrationCard — SMART Build 2A
//
// Compact tenant EHR (SMART) integration config card. Never-PHI, never-secret.
//   - platform_admin: create / edit / disable / test capability (canEdit=true)
//   - tenant_admin:   read-only same-tenant summary
//
// All reads/writes go through gp-free admin callables (ehrApi). No direct
// Firestore writes; no tokens/secrets/PHI; no GP launch button.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import { Plug, Shield, Loader2, CheckCircle, AlertCircle, RefreshCw, Ban, Copy, Check, ExternalLink, FlaskConical, Power } from 'lucide-react';
import {
  listTenantEhrConnections,
  upsertTenantEhrConnection,
  disableTenantEhrConnection,
  testTenantEhrConnectionCapability,
  listTenantEhrUserMappings,
  upsertTenantEhrUserMapping,
  disableTenantEhrUserMapping,
} from '../core/ehrApi.js';
import {
  buildSandboxConsoleModel,
  computeEhrActivationReadiness,
  SMART_APP_LAUNCHER_URL,
  PASS_A_BASELINE,
  PASS_B_ENFORCED,
  POST_LAUNCH_CHECKLIST,
  FAILURE_GUIDE,
  SMART_LAUNCHER_INSTRUCTIONS,
} from '../core/smartSandboxConsole.js';
import EhrItSetupGuidePanel from './EhrItSetupGuidePanel.jsx';

const DEFAULT_SCOPES = 'launch openid fhirUser patient/Patient.r patient/Coverage.rs';
// Build 2J P1 — EHR Connection Activation Gate (client mirror of the server gate).
const CANONICAL_CALLBACK = 'https://referral-gate.web.app/ehr/smart/callback';
const ACTIVATION_ERROR_MESSAGES = {
  'activation-precondition-capability': 'Run "Test capability" and get a green (OK) result before setting this connection ACTIVE.',
  'invalid-redirecturi-exact': `Redirect URI must exactly equal ${CANONICAL_CALLBACK} (use the exact canonical callback, or keep Sandbox mode on).`,
  // Build 2J P3 — production issuer allowlist (required whenever status → ACTIVE).
  'production-issuer-allowlist-missing': 'This FHIR issuer must be added to the production issuer allowlist (EHR_PRODUCTION_ISSUER_ALLOWLIST) before this connection can be activated.',
  'production-issuer-not-allowed': 'This connection’s FHIR issuer is not in the production issuer allowlist. Add it before activating.',
  'invalid-production-issuer-allowlist': 'The production issuer allowlist is misconfigured. Fix EHR_PRODUCTION_ISSUER_ALLOWLIST before activating.',
  // Unsafe / invalid scopes (validated when the connection doc is rebuilt).
  'forbidden-scope': 'This connection requests an unsafe scope (wildcard / write / user / system / offline_access). Remove it before activating.',
  'invalid-scope': 'This connection has an invalid requested scope. Fix the scopes before activating.',
  // RBAC.
  'permission-denied': 'You do not have permission to activate this connection (platform admin only).',
};
const AUTH_TYPES = [
  { value: 'public_pkce', label: 'Public + PKCE (no secret)' },
  { value: 'confidential_client', label: 'Confidential client (later)' },
  { value: 'backend_services_later', label: 'Backend services (later)' },
];
const STATUS_BADGE = {
  TESTING: 'bg-amber-500/20 text-amber-300',
  ACTIVE: 'bg-emerald-500/20 text-emerald-300',
  DISABLED: 'bg-slate-600/40 text-slate-300',
};

const emptyForm = {
  connectionId: '',
  sourceSystemLabel: '',
  fhirBaseUrl: '',
  clientId: '',
  launchUrl: '',
  redirectUri: '',
  requestedScopes: DEFAULT_SCOPES,
  authType: 'public_pkce',
  sandboxMode: true,
  requireUserMapping: false,
  status: 'TESTING',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-medium text-slate-300 mb-1">{label}{hint && <span className="ml-2 text-slate-500 font-normal">{hint}</span>}</label>
    {children}
  </div>
);

const inputCls = 'w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-sky-500/50 disabled:opacity-60';

const EhrIntegrationCard = ({ tenantId, canEdit = false }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [connection, setConnection] = useState(null); // stored doc (or null)
  const [form, setForm] = useState(emptyForm);
  const [cardMappings, setCardMappings] = useState([]); // safe (redacted) mappings — gates Activate

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await listTenantEhrConnections({ tenantId });
      const list = Array.isArray(res?.connections) ? res.connections : [];
      const conn = list[0] || null; // v1: a single connection per tenant card
      setConnection(conn);
      if (conn) {
        setForm({
          connectionId: conn.connectionId || '',
          sourceSystemLabel: conn.sourceSystemLabel || '',
          fhirBaseUrl: conn.fhirBaseUrl || '',
          clientId: conn.clientId || '',
          launchUrl: conn.launchUrl || '',
          redirectUri: conn.redirectUri || '',
          requestedScopes: Array.isArray(conn.requestedScopes) ? conn.requestedScopes.join(' ') : DEFAULT_SCOPES,
          authType: conn.authType || 'public_pkce',
          sandboxMode: conn.sandboxMode !== false,
          requireUserMapping: conn.requireUserMapping === true,
          status: conn.status || 'TESTING',
        });
      } else {
        setForm(emptyForm);
      }
      // Active user-mapping count gates the Activate control (server stays authoritative).
      if (conn) {
        try {
          const mres = await listTenantEhrUserMappings({ tenantId, connectionId: conn.connectionId });
          setCardMappings(Array.isArray(mres?.mappings) ? mres.mappings : []);
        } catch (_) {
          setCardMappings([]); // unknown → treat as none (Activate hidden; safe default)
        }
      } else {
        setCardMappings([]);
      }
    } catch (e) {
      setError(safeUserError(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Refresh ONLY the mapping count (no full-card spinner) after a mapping changes,
  // so the Activate control appears/disappears immediately.
  const refreshMappings = useCallback(async () => {
    if (!tenantId || !connection?.connectionId) return;
    try {
      const mres = await listTenantEhrUserMappings({ tenantId, connectionId: connection.connectionId });
      setCardMappings(Array.isArray(mres?.mappings) ? mres.mappings : []);
    } catch (_) { /* keep prior count on transient error */ }
  }, [tenantId, connection?.connectionId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!canEdit) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      const connectionPayload = {
        connectionId: form.connectionId || undefined,
        providerType: 'smart',
        status: form.status || 'TESTING',
        sourceSystemLabel: form.sourceSystemLabel || null,
        fhirBaseUrl: form.fhirBaseUrl,
        clientId: form.clientId || null,
        launchUrl: form.launchUrl || null,
        redirectUri: form.redirectUri || null,
        requestedScopes: form.requestedScopes.split(/\s+/).map((s) => s.trim()).filter(Boolean),
        authType: form.authType,
        sandboxMode: !!form.sandboxMode,
        requireUserMapping: !!form.requireUserMapping,
      };
      const res = await upsertTenantEhrConnection({ tenantId, connection: connectionPayload });
      setSuccess(`Saved connection (${res.status}).`);
      await load();
    } catch (e) {
      // Build 2J P1 — surface the EXACT unmet activation precondition (fail-fast).
      const code = e?.details?.code;
      setError(ACTIVATION_ERROR_MESSAGES[code] || safeUserError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    if (!canEdit || !connection) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      const res = await testTenantEhrConnectionCapability({ tenantId, connectionId: connection.connectionId });
      const s = res.summary || {};
      setSuccess(`Capability OK — FHIR ${s.fhirVersion || '?'}, ${s.resourceCount ?? '?'} resources` +
        `${s.patientSupported ? ', Patient ✓' : ''}${s.coverageSupported ? ', Coverage ✓' : ''}.`);
      await load();
    } catch (e) {
      setError(safeUserError(e));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!canEdit || !connection) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      await disableTenantEhrConnection({ tenantId, connectionId: connection.connectionId });
      setSuccess('Connection disabled.');
      await load();
    } catch (e) {
      setError(safeUserError(e));
    } finally {
      setBusy(false);
    }
  };

  // Switch TESTING → ACTIVE. Re-sends the FULL existing connection payload with
  // status:'ACTIVE' through the SAME admin callable (no direct Firestore write).
  // The server gate (platform_admin + capability OK + exact redirect + production
  // issuer allowlist + safe scopes) is authoritative and stamps activatedAt /
  // activatedByUid. sandboxMode is preserved (stays true for the test connection).
  const handleActivate = async () => {
    if (!canEdit || !connection) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      const connectionPayload = {
        connectionId: connection.connectionId,
        providerType: 'smart',
        status: 'ACTIVE',
        sourceSystemLabel: connection.sourceSystemLabel || null,
        fhirBaseUrl: connection.fhirBaseUrl,
        clientId: connection.clientId || null,
        launchUrl: connection.launchUrl || null,
        redirectUri: connection.redirectUri || null,
        requestedScopes: Array.isArray(connection.requestedScopes)
          ? connection.requestedScopes
          : (typeof connection.requestedScopes === 'string' ? connection.requestedScopes.split(/\s+/).map((s) => s.trim()).filter(Boolean) : []),
        authType: connection.authType || 'public_pkce',
        sandboxMode: connection.sandboxMode !== false, // preserved (true for the test connection)
        requireUserMapping: connection.requireUserMapping === true,
      };
      const res = await upsertTenantEhrConnection({ tenantId, connection: connectionPayload });
      setSuccess(`Connection is now ${res.status} — EHR launch is live for mapped GPs.`);
      await load();
    } catch (e) {
      // Fail-fast: surface the EXACT safe activation reason.
      const raw = e?.details?.code || e?.code || '';
      const code = String(raw).replace(/^functions\//, '');
      setError(ACTIVATION_ERROR_MESSAGES[code] || safeUserError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!tenantId) return null;

  const lastSummary = connection?.lastCapabilitySummary || null;

  // Build 2J P1 — client-side activation pre-check (fail-fast mirror of the server
  // gate): a transition to ACTIVE needs a green capability test, and a production
  // (sandbox-off) connection needs the EXACT canonical redirect URI. Blocks a
  // doomed save so the UI never shows a false success.
  const capabilityGreen = !!connection && typeof connection.lastCapabilityStatus === 'string'
    && connection.lastCapabilityStatus.toUpperCase() === 'OK';
  const activationIssues = [];
  if (form.status === 'ACTIVE') {
    if (!capabilityGreen) activationIssues.push('Capability test must be green (OK). Run "Test capability" first.');
    if (form.sandboxMode === false && (form.redirectUri || '').trim() !== CANONICAL_CALLBACK) {
      activationIssues.push(`Production redirect URI must exactly equal ${CANONICAL_CALLBACK}.`);
    }
  }
  const activationBlocked = form.status === 'ACTIVE' && activationIssues.length > 0;

  // Dedicated "Activate connection" control — visible only when EVERY client-visible
  // precondition holds (TESTING + capability OK + exact redirect + fhirBaseUrl +
  // requireUserMapping ON + >=1 ACTIVE mapping). The server gate stays authoritative.
  const activation = computeEhrActivationReadiness({ connection, mappings: cardMappings });

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden mb-6">
      <div className="p-4 border-b border-slate-700/50 bg-slate-800/40">
        <div className="flex items-center gap-3">
          <Plug className="w-5 h-5 text-sky-400" />
          <h2 className="text-lg font-semibold text-white">EHR Integration (SMART)</h2>
          {connection && (
            <span className={`ml-1 px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_BADGE[connection.status] || STATUS_BADGE.TESTING}`}>
              {connection.status}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Configure the tenant&apos;s FHIR/SMART connection. Prepares for EHR Launch; no launch/token/patient import here.
        </p>
        <div className="mt-2 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-sky-500/10 border border-sky-500/20 w-fit">
          <Shield className="w-3.5 h-3.5 text-sky-400" />
          <span className="text-[11px] text-blue-700 font-medium">No tokens or patient data are stored here.</span>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {!canEdit && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg w-fit">
                <Shield className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-amber-400 font-medium">Read-only — managed by Platform Admin</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Source system label">
                <input className={inputCls} value={form.sourceSystemLabel} disabled={!canEdit || busy}
                  onChange={(e) => setField('sourceSystemLabel', e.target.value)} placeholder="SMART R4 Sandbox" />
              </Field>
              <Field label="FHIR base URL" hint="https">
                <input className={inputCls} value={form.fhirBaseUrl} disabled={!canEdit || busy}
                  onChange={(e) => setField('fhirBaseUrl', e.target.value)} placeholder="https://r4.smarthealthit.org" />
              </Field>
              <Field label="Client ID">
                <input className={inputCls} value={form.clientId} disabled={!canEdit || busy}
                  onChange={(e) => setField('clientId', e.target.value)} placeholder="public client id" />
              </Field>
              <Field label="Auth type">
                <select className={inputCls} value={form.authType} disabled={!canEdit || busy}
                  onChange={(e) => setField('authType', e.target.value)}>
                  {AUTH_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </Field>
              <Field label="Launch URL" hint="ReferralGate host">
                <input className={inputCls} value={form.launchUrl} disabled={!canEdit || busy}
                  onChange={(e) => setField('launchUrl', e.target.value)} placeholder="https://referral-gate.web.app/ehr/smart/launch" />
              </Field>
              <Field label="Redirect URI" hint="ReferralGate host">
                <input className={inputCls} value={form.redirectUri} disabled={!canEdit || busy}
                  onChange={(e) => setField('redirectUri', e.target.value)} placeholder="https://referral-gate.web.app/ehr/smart/callback" />
              </Field>
              <Field label="Status" hint="ACTIVE needs a green capability test">
                <select className={inputCls} value={form.status} disabled={!canEdit || busy}
                  onChange={(e) => setField('status', e.target.value)}>
                  <option value="TESTING">TESTING</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="DISABLED">DISABLED</option>
                </select>
              </Field>
            </div>

            <Field label="Requested scopes" hint="space-separated; read-only scopes only">
              <input className={inputCls} value={form.requestedScopes} disabled={!canEdit || busy}
                onChange={(e) => setField('requestedScopes', e.target.value)} placeholder={DEFAULT_SCOPES} />
            </Field>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={!!form.sandboxMode} disabled={!canEdit || busy}
                  onChange={(e) => setField('sandboxMode', e.target.checked)} />
                Sandbox mode
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300" title="Require an EHR user↔GP mapping before a SMART launch can create a packet or import. Enable for ACTIVE connections.">
                <input type="checkbox" checked={!!form.requireUserMapping} disabled={!canEdit || busy}
                  onChange={(e) => setField('requireUserMapping', e.target.checked)} />
                Require EHR user mapping
              </label>
              {connection && (
                <div className="text-xs text-slate-500">
                  Last capability: <span className={connection.lastCapabilityStatus === 'ok' ? 'text-emerald-400' : connection.lastCapabilityStatus === 'error' ? 'text-red-400' : 'text-slate-400'}>
                    {connection.lastCapabilityStatus || 'not checked'}
                  </span>
                  {lastSummary?.fhirVersion ? ` · FHIR ${lastSummary.fhirVersion} · ${lastSummary.resourceCount ?? '?'} resources` : ''}
                </div>
              )}
            </div>

            {canEdit && activationBlocked && (
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-300">
                  <p className="font-medium mb-1">Resolve before setting this connection ACTIVE:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {activationIssues.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                </div>
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </div>
            )}
            {success && (
              <div className="flex items-start gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-emerald-400">{success}</p>
              </div>
            )}

            {/* Activate readiness hint — why the Activate control is not yet available. */}
            {canEdit && activation.isTesting && !activation.canActivate && (
              <div className="flex items-start gap-2 p-3 bg-slate-700/20 border border-slate-600/40 rounded-lg">
                <Power className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-slate-300">
                  <p className="font-medium mb-1">To activate this connection (switch EHR launch from testing to live), it still needs:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {activation.unmet.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {canEdit && (
              <div className="flex items-center gap-2 pt-1">
                <button type="button" onClick={handleSave} disabled={busy || !form.fhirBaseUrl || activationBlocked}
                  title={activationBlocked ? 'Resolve the activation requirements above before saving as ACTIVE' : undefined}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Save connection
                </button>
                <button type="button" onClick={handleTest} disabled={busy || !connection}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-200 hover:bg-slate-800 disabled:opacity-50">
                  <RefreshCw className="w-4 h-4" /> Test capability
                </button>
                <button type="button" onClick={handleDisable} disabled={busy || !connection || connection.status === 'DISABLED'}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-red-700/50 text-red-300 hover:bg-red-900/20 disabled:opacity-50">
                  <Ban className="w-4 h-4" /> Disable
                </button>
                {activation.canActivate && (
                  <button type="button" onClick={handleActivate} disabled={busy}
                    title="Switch this EHR connection from testing to active — SMART launch becomes live for mapped GPs"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />} Activate connection
                  </button>
                )}
              </div>
            )}

            {/* Build 2I — EHR user mappings. tenant_admin + platform_admin may
                manage (server enforces); the connection form may be read-only for
                tenant_admin, but identity binding is tenant-operational. */}
            {connection && (
              <EhrUserMappingsPanel tenantId={tenantId} connectionId={connection.connectionId} canManage onMappingsChanged={refreshMappings} />
            )}

            {/* Build 2K — Sandbox Launch Console (read-only operator surface). */}
            {connection && (
              <SmartSandboxConsolePanel tenantId={tenantId} connection={connection} />
            )}

            {/* Build 2P — IT Setup Guide / EHR Integration Onboarding Pack.
                Read/copy-only; rendered even before a connection exists so the
                static onboarding content is available during setup. */}
            <EhrItSetupGuidePanel tenantId={tenantId} connection={connection} />
          </>
        )}
      </div>
    </div>
  );
};

// ── Build 2I — EHR User Mappings panel ────────────────────────────────────────
// Binds an EHR fhirUser to an active same-tenant gp_employee uid. Hash-only:
// the list shows a redacted fhirUserDisplay; the raw fhirUser/hash are never shown.
const EhrUserMappingsPanel = ({ tenantId, connectionId, canManage = false, onMappingsChanged }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [fhirUser, setFhirUser] = useState('');
  const [gpUid, setGpUid] = useState('');

  const load = useCallback(async () => {
    if (!tenantId || !connectionId) { setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const res = await listTenantEhrUserMappings({ tenantId, connectionId });
      setMappings(Array.isArray(res?.mappings) ? res.mappings : []);
    } catch (e) {
      setErr(e?.message || 'Failed to load EHR user mappings.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, connectionId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!canManage || !fhirUser.trim() || !gpUid.trim()) return;
    setBusy(true); setErr('');
    try {
      await upsertTenantEhrUserMapping({ tenantId, connectionId, fhirUser: fhirUser.trim(), referralGateUid: gpUid.trim() });
      setFhirUser(''); setGpUid('');
      await load();
      if (onMappingsChanged) await onMappingsChanged();
    } catch (e) {
      setErr(e?.message || 'Failed to add mapping.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async (mappingId) => {
    if (!canManage) return;
    setBusy(true); setErr('');
    try {
      await disableTenantEhrUserMapping({ tenantId, connectionId, mappingId });
      await load();
      if (onMappingsChanged) await onMappingsChanged();
    } catch (e) {
      setErr(e?.message || 'Failed to disable mapping.');
    } finally {
      setBusy(false);
    }
  };

  const cls = 'w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-sky-500/50 disabled:opacity-60';

  return (
    <div className="mt-5 pt-4 border-t border-slate-700/50">
      <div className="flex items-center gap-2 mb-1">
        <Shield className="w-4 h-4 text-sky-400" />
        <h3 className="text-sm font-semibold text-white">EHR User Mappings</h3>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Link an EHR user (fhirUser) to a referring provider. Enforced only when “Require EHR user mapping” is on. Stored hash-only — the raw EHR user value is never shown or saved.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading mappings…</div>
      ) : (
        <>
          {mappings.length === 0 ? (
            <div className="text-xs text-slate-500 mb-3">No mappings yet.</div>
          ) : (
            <ul className="space-y-2 mb-3">
              {mappings.map((m) => (
                <li key={m.mappingId} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-900/40 border border-slate-700/50">
                  <div className="min-w-0">
                    <div className="text-xs text-slate-200 font-medium truncate">{m.fhirUserDisplay || 'EHR user'}</div>
                    <div className="text-[11px] text-slate-500 truncate">GP: <span className="font-mono">{m.referralGateUid}</span></div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${m.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-600/40 text-slate-300'}`}>{m.status}</span>
                    {canManage && m.status === 'ACTIVE' && (
                      <button type="button" onClick={() => handleDisable(m.mappingId)} disabled={busy}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-red-700/50 text-red-300 hover:bg-red-900/20 disabled:opacity-50">
                        <Ban className="w-3 h-3" /> Disable
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {canManage && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
              <div className="sm:col-span-1">
                <label className="block text-[11px] font-medium text-slate-300 mb-1">EHR user (fhirUser)</label>
                <input className={cls} value={fhirUser} disabled={busy} onChange={(e) => setFhirUser(e.target.value)} placeholder="Practitioner/abc-123" />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-[11px] font-medium text-slate-300 mb-1">Referring provider UID</label>
                <input className={cls} value={gpUid} disabled={busy} onChange={(e) => setGpUid(e.target.value)} placeholder="gp user uid" />
              </div>
              <button type="button" onClick={handleAdd} disabled={busy || !fhirUser.trim() || !gpUid.trim()}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Link user
              </button>
            </div>
          )}

          {err && (
            <div className="flex items-start gap-2 mt-3 p-2.5 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-red-400">{err}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Build 2K — SMART Sandbox Test Console ─────────────────────────────────────
// Read-only operator surface: copyable launch/redirect/scopes values + mapping
// readiness + test checklists + failure guide. Reads only safe config + the safe
// (redacted) mapping summary. Shows NO tokens, PHI, raw fhirUser, or candidate
// values. The real launch still starts at /ehr/smart/launch via a SMART launcher.
const SmartSandboxConsolePanel = ({ tenantId, connection }) => {
  const [mappings, setMappings] = useState([]);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!tenantId || !connection?.connectionId) return;
      try {
        const res = await listTenantEhrUserMappings({ tenantId, connectionId: connection.connectionId });
        if (alive) setMappings(Array.isArray(res?.mappings) ? res.mappings : []);
      } catch (_) { if (alive) setMappings([]); }
    })();
    return () => { alive = false; };
  }, [tenantId, connection?.connectionId]);

  const model = buildSandboxConsoleModel({ tenantId, connection, mappings });

  const copy = async (key, value) => {
    try {
      if (navigator?.clipboard?.writeText) await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? '' : k)), 1500);
    } catch (_) { /* clipboard unavailable — value remains visible to copy manually */ }
  };

  const CopyField = ({ label, value, fieldKey }) => (
    <div>
      <div className="text-[11px] font-medium text-slate-300 mb-1">{label}</div>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 min-w-0 px-2 py-1.5 bg-slate-900/60 border border-slate-700 rounded text-[11px] text-blue-700 break-all">{value || '—'}</code>
        <button type="button" onClick={() => copy(fieldKey, value || '')} disabled={!value}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-600 text-slate-200 hover:bg-slate-800 disabled:opacity-40">
          {copied === fieldKey ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied === fieldKey ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );

  // Static class map — Tailwind JIT cannot see dynamically-built class names.
  const TONE_CLS = {
    slate: 'bg-slate-500/15 text-slate-300',
    emerald: 'bg-emerald-500/15 text-emerald-300',
    amber: 'bg-amber-500/15 text-amber-300',
    sky: 'bg-sky-500/15 text-blue-700',
  };
  const Chip = ({ label, value, tone = 'slate' }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${TONE_CLS[tone] || TONE_CLS.slate}`}>
      {label}: {value}
    </span>
  );

  return (
    <div className="mt-5 pt-4 border-t border-slate-700/50">
      <div className="flex items-center gap-2 mb-1">
        <FlaskConical className="w-4 h-4 text-sky-400" />
        <h3 className="text-sm font-semibold text-white">SMART Sandbox Test Console</h3>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Copy these into the external SMART App Launcher / EHR sandbox. The real launch starts there and calls the Launch URL with <code>iss</code> + <code>launch</code>. No tokens or PHI are shown here.
      </p>

      <div className="space-y-3">
        <CopyField label="Launch URL (register in EHR / launcher)" value={model.launchUrl} fieldKey="launch" />
        <CopyField label="Redirect URI" value={model.redirectUri} fieldKey="redirect" />
        <CopyField label="Required scopes" value={model.scopes} fieldKey="scopes" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <CopyField label="FHIR base URL" value={model.fhirBaseUrl} fieldKey="fhir" />
          <CopyField label="Client ID" value={model.clientId} fieldKey="client" />
        </div>
      </div>

      {/* Connection mode + mapping readiness */}
      <div className="mt-3 flex flex-wrap gap-2">
        <Chip label="status" value={model.status || '—'} tone={model.status === 'ACTIVE' ? 'emerald' : model.status === 'DISABLED' ? 'slate' : 'amber'} />
        <Chip label="sandboxMode" value={String(model.sandboxMode)} />
        <Chip label="requireUserMapping" value={String(model.requireUserMapping)} tone={model.requireUserMapping ? 'sky' : 'slate'} />
        {model.sourceSystemLabel && <Chip label="source" value={model.sourceSystemLabel} />}
        <Chip label="mappings" value={`${model.mapping.active} active / ${model.mapping.disabled} disabled`} tone={model.mapping.active > 0 ? 'emerald' : 'slate'} />
      </div>
      <div className="mt-2 text-[11px] text-slate-400">
        {model.requireUserMapping
          ? (model.enforcedReady
            ? '✓ Mapping enforced and at least one ACTIVE mapping exists.'
            : '⚠ requireUserMapping is ON but no ACTIVE mapping exists — mapped GPs will be blocked until you add one.')
          : 'Baseline mode: requireUserMapping is OFF — launch/import allowed without mapping. Enable it only after mappings exist.'}
      </div>

      {/* External launcher */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <a href={SMART_APP_LAUNCHER_URL} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-sky-600 text-white hover:bg-sky-700">
          <ExternalLink className="w-3.5 h-3.5" /> Open SMART App Launcher
        </a>
        <span className="text-[11px] text-slate-500">Opens launch.smarthealthit.org in a new tab.</span>
      </div>
      <ol className="mt-2 list-decimal list-inside text-[11px] text-slate-400 space-y-0.5">
        {SMART_LAUNCHER_INSTRUCTIONS.map((s) => <li key={s}>{s}</li>)}
      </ol>

      {/* Test passes */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] font-semibold text-slate-200 mb-1">Pass A — Baseline</div>
          <ul className="text-[11px] text-slate-400 space-y-0.5">
            {PASS_A_BASELINE.map((s) => <li key={s}>☐ {s}</li>)}
          </ul>
        </div>
        <div>
          <div className="text-[11px] font-semibold text-slate-200 mb-1">Pass B — Mapping enforced</div>
          <ul className="text-[11px] text-slate-400 space-y-0.5">
            {PASS_B_ENFORCED.map((s) => <li key={s}>☐ {s}</li>)}
          </ul>
        </div>
      </div>

      {/* Post-launch checklist */}
      <div className="mt-4">
        <div className="text-[11px] font-semibold text-slate-200 mb-1">After launch — inspect (no PHI values shown here)</div>
        <ul className="text-[11px] text-slate-400 space-y-0.5">
          {POST_LAUNCH_CHECKLIST.map((s) => <li key={s}>☐ {s}</li>)}
        </ul>
      </div>

      {/* Failure guide */}
      <div className="mt-4">
        <div className="text-[11px] font-semibold text-slate-200 mb-1">Failure guide</div>
        <ul className="text-[11px] text-slate-400 space-y-0.5">
          {FAILURE_GUIDE.map((g) => (
            <li key={g.code}><code className="text-amber-300">{g.code}</code> — {g.hint}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default EhrIntegrationCard;
